// src/services/firebasePhone.service.js
// Firebase Phone Authentication Service

import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth, isConfigured } from '../config/firebase.config';

class FirebasePhoneService {
    constructor() {
        this.confirmationResult = null;
        this.recaptchaVerifier = null;
    }

    /**
     * Initialize invisible reCAPTCHA verifier
     * @param {string} containerId - The ID of the button/element for reCAPTCHA
     */
    initRecaptcha(containerId = 'recaptcha-container') {
        if (!isConfigured || !auth) {
            console.error('Firebase not configured');
            return false;
        }

        try {
            // Clear existing verifier if any
            if (this.recaptchaVerifier) {
                try {
                    this.recaptchaVerifier.clear();
                } catch (e) {
                    // Ignore clear errors
                }
                this.recaptchaVerifier = null;
            }

            // IMPORTANT: Clear the DOM container to prevent "already rendered" error
            const container = document.getElementById(containerId);
            if (container) {
                container.innerHTML = '';
            }

            this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
                size: 'invisible',
                callback: () => {
                    console.log('reCAPTCHA verified');
                },
                'expired-callback': () => {
                    console.log('reCAPTCHA expired');
                    this.recaptchaVerifier = null;
                }
            });

            return true;
        } catch (error) {
            console.error('Error initializing reCAPTCHA:', error);
            return false;
        }
    }

    /**
     * Send OTP to phone number using Firebase
     * @param {string} phoneNumber - Phone number with country code (+919876543210)
     * @returns {Promise<object>} - Result object
     */
    async sendOTP(phoneNumber) {
        if (!isConfigured || !auth) {
            throw new Error('Firebase not configured. Please check your environment variables.');
        }

        // Format phone number
        const formattedPhone = this.formatPhoneNumber(phoneNumber);
        console.log('📱 Sending OTP via Firebase to:', formattedPhone);

        try {
            // Initialize reCAPTCHA if not already done
            if (!this.recaptchaVerifier) {
                this.initRecaptcha('recaptcha-container');
            }

            // Send OTP
            this.confirmationResult = await signInWithPhoneNumber(
                auth,
                formattedPhone,
                this.recaptchaVerifier
            );

            console.log('✅ OTP sent successfully via Firebase');
            return {
                success: true,
                message: 'OTP sent to your mobile number'
            };
        } catch (error) {
            console.error('❌ Firebase OTP error:', error);

            // Reset reCAPTCHA on error
            if (this.recaptchaVerifier) {
                try {
                    this.recaptchaVerifier.clear();
                } catch (e) {
                    // Ignore clear errors
                }
                this.recaptchaVerifier = null;
            }

            // Handle specific Firebase errors
            let errorMessage = 'Failed to send OTP. Please try again.';
            switch (error.code) {
                case 'auth/invalid-phone-number':
                    errorMessage = 'Invalid phone number format. Please use +91XXXXXXXXXX.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many attempts. Please try again later.';
                    break;
                case 'auth/quota-exceeded':
                    errorMessage = 'SMS quota exceeded. Please try again later.';
                    break;
                case 'auth/captcha-check-failed':
                    errorMessage = 'reCAPTCHA verification failed. Please refresh and try again.';
                    break;
                default:
                    errorMessage = error.message || errorMessage;
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Verify OTP entered by user
     * @param {string} otp - 6-digit OTP
     * @returns {Promise<object>} - Firebase user credential
     */
    async verifyOTP(otp) {
        if (!this.confirmationResult) {
            throw new Error('No OTP request found. Please request OTP first.');
        }

        console.log('🔐 Verifying OTP via Firebase...');

        try {
            const result = await this.confirmationResult.confirm(otp);
            console.log('✅ OTP verified successfully');

            // Get Firebase ID token for backend verification
            const idToken = await result.user.getIdToken();

            return {
                success: true,
                user: result.user,
                idToken: idToken,
                phoneNumber: result.user.phoneNumber
            };
        } catch (error) {
            console.error('❌ OTP verification failed:', error);

            let errorMessage = 'Invalid OTP. Please try again.';
            switch (error.code) {
                case 'auth/invalid-verification-code':
                    errorMessage = 'Invalid OTP. Please check and try again.';
                    break;
                case 'auth/code-expired':
                    errorMessage = 'OTP has expired. Please request a new one.';
                    break;
                default:
                    errorMessage = error.message || errorMessage;
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Format phone number to E.164 format
     * @param {string} phone - Phone number
     * @returns {string} - Formatted phone number
     */
    formatPhoneNumber(phone) {
        // Remove all non-digit characters except +
        let cleaned = phone.replace(/[^\d+]/g, '');

        // If starts with +, return as is
        if (cleaned.startsWith('+')) {
            return cleaned;
        }

        // If 10 digits, add +91 (India)
        if (cleaned.length === 10) {
            return '+91' + cleaned;
        }

        // If 12 digits starting with 91, add +
        if (cleaned.length === 12 && cleaned.startsWith('91')) {
            return '+' + cleaned;
        }

        // Default: add +91
        return '+91' + cleaned.slice(-10);
    }

    /**
     * Reset the service state
     */
    reset() {
        this.confirmationResult = null;
        if (this.recaptchaVerifier) {
            try {
                this.recaptchaVerifier.clear();
            } catch (e) {
                // Ignore clear errors
            }
            this.recaptchaVerifier = null;
        }
    }
}

export default new FirebasePhoneService();
